import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { processImageUrl } from '../../utils/image';
import Image from '../Image/Image';
import ProductBadge from '../ProductBadge/ProductBadge';
import CustomProductBadge from '../CustomProductBadge/CustomProductBadge';
import PIMBadge from '../PIMBadge/PIMBadge';
import useStyles from './ProductImage.styles';
import SalesBadge from '../SalesBadge/SalesBadge';
const IMAGE_PRODUCT_PLACEHOLDER = 'https://cdn.media.amplience.net/i/interflora/not-available';
const CUSTOM_BADGE_SCHEMA = 'https://interflora.co.uk/content-type/custom-product-badge';
function isCustomBadgeSchema(badge) {
    var _a;
    return ((_a = badge === null || badge === void 0 ? void 0 : badge._meta) === null || _a === void 0 ? void 0 : _a.schema) === CUSTOM_BADGE_SCHEMA;
}
function convertBadgePositionToAlignment(position) {
    if (!position)
        return 'topLeft';
    const positionMap = {
        TOP_LEFT: 'topLeft',
        TOP_RIGHT: 'topRight',
        BOTTOM_LEFT: 'bottomLeft',
        BOTTOM_RIGHT: 'bottomRight',
    };
    return positionMap[position] || 'topLeft';
}
export function filterImageWidths(imageWidths, maxWidth) {
    const index = imageWidths.findIndex((width) => width >= maxWidth);
    const filteredImageWidths = index !== -1 ? imageWidths.slice(0, 1 + index) : [...imageWidths, maxWidth];
    return filteredImageWidths;
}
const ProductImage = (props) => {
    const { classes } = useStyles();
    const { imageUrl, altText, label, badge, pimBadge, badgeSmall, classNames, style, maxWidth = 480, sizes, imageWidths = [240, 384, 480, 960, 1280, 1600], salesBadge, isFromProductCard, onClick, isFromSpotlightNavigation, layout, priority, isFromBlogArticleCard, country = 'UK', isCarousel = false, id, useOriginalImage, isFromProductDetail = false, isFromProductDetailV2 = false, badgeOffset, isPortraitCard = false, isHamperCard = false, imageHeight, } = props;
    const processedUrl = processImageUrl(imageUrl || IMAGE_PRODUCT_PLACEHOLDER, { w: maxWidth, img404: 'not-available' }, ['$poi-square$']);
    // Get all the image sizes less than the max height except 1
    const filteredImageWidths = filterImageWidths(imageWidths, maxWidth);
    // Check if badge is custom schema and prepare CustomProductBadge props
    const getCustomProductBadgeProps = () => {
        var _a, _b, _c;
        if (badge && isCustomBadgeSchema(badge)) {
            // Calculate variant based on available content
            const hasText = !!badge.badgeText;
            const hasImage = !!((_a = badge.badgeImage) === null || _a === void 0 ? void 0 : _a.url); // Check if image URL exists
            let variant = 'textOnly';
            if (hasText && hasImage) {
                variant = 'textWithImage';
            }
            else if (hasImage && !hasText) {
                variant = 'imageOnly';
            }
            return {
                badgeTextPosition: convertBadgePositionToAlignment(badge.badgePosition || undefined),
                badgeImagePosition: convertBadgePositionToAlignment(badge.badgePositionImage || undefined),
                backgroundColor: badge.backgroundColour || undefined,
                textColor: badge.textColour || undefined,
                badgeTextAlignment: badge.badgeTextAlignment,
                badgeText: badge.badgeText || undefined,
                imageSrc: (_b = badge.badgeImage) === null || _b === void 0 ? void 0 : _b.url,
                imageAlt: ((_c = badge.badgeImage) === null || _c === void 0 ? void 0 : _c.altText) || '',
                site: badge.site || undefined,
                badgeStyle: badge.badgeStyle || 'Square block',
                isCarousel,
                isFromProductCard,
                variant,
                isFromProductDetail,
                isFromProductDetailV2,
            };
        }
        return null;
    };
    const customProductBadgeProps = getCustomProductBadgeProps();
    const regularBadgeProps = badge && !isCustomBadgeSchema(badge) ? badge : null;
    const renderBadge = () => {
        // Badge priority: PIMBadge > CustomProductBadge > ProductBadge
        if (pimBadge && (pimBadge.badgeTextLine1 || pimBadge.badgeTextLine2)) {
            return (_jsx("div", { className: classes.pimBadge, children: _jsx(PIMBadge, { pimBadge: pimBadge, isCarousel: isCarousel, isFromProductCard: isFromProductCard, isFromProductDetail: isFromProductDetail, isFromProductDetailV2: isFromProductDetailV2 }) }));
        }
        if (customProductBadgeProps) {
            return _jsx(CustomProductBadge, Object.assign({}, customProductBadgeProps));
        }
        if (regularBadgeProps) {
            return (_jsx("div", { className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.badge, classes.badge), style: badgeOffset !== undefined ? { top: `${badgeOffset}px`, left: `${badgeOffset}px` } : undefined, children: _jsx(ProductBadge, Object.assign({}, regularBadgeProps, { small: badgeSmall })) }));
        }
        return null;
    };
    return (_jsxs("div", { className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.container, classes.container, isFromProductDetailV2 && classes.newImageContainer), style: style, id: id, children: [_jsxs("div", { className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.imageContainer, classes.imageContainer, isPortraitCard && classes.portraitFillContainer), "data-testid": isFromSpotlightNavigation ? processedUrl : undefined, children: [_jsx(Image, { imageUrl: useOriginalImage && imageUrl ? imageUrl : processedUrl, sizes: sizes, 
                        // only include image widths if there is more than 1
                        imageWidths: filteredImageWidths.length > 1 ? filteredImageWidths : undefined, altText: altText || '', className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.image, classes.image, isFromBlogArticleCard && classes.imageBlogArticleCard), draggable: false, width: maxWidth, 
                        // Omit height for portrait cards so NextImage uses fill mode (position:absolute,
                        // 100% × 100%) and the image covers the container without distortion.
                        height: imageHeight !== null && imageHeight !== void 0 ? imageHeight : maxWidth, onClick: onClick, priority: priority }), renderBadge(), !!salesBadge && (_jsx("div", { className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.badge, classes.badge), style: badgeOffset !== undefined ? { top: `${badgeOffset}px`, left: `${badgeOffset}px` } : undefined, children: _jsx(SalesBadge, Object.assign({}, salesBadge, { isFromProductCard: isFromProductCard })) }))] }), label && (_jsx("div", { className: clsx(classNames === null || classNames === void 0 ? void 0 : classNames.label, classes.label, isFromProductDetailV2 && classes.newImageLabelContainer), children: _jsx("span", { children: label }) }, imageUrl))] }));
};
export default ProductImage;
